import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { APP_STORE_URL } from "@/lib/constants";

const monthlyFeatures = [
  "AI Sabbat Assistant",
  "Personlig sabbatårsplan",
  "Job og rejse inspiration",
  "Budget og økonomiværktøjer",
  "CV builder",
  "Portfolio builder",
  "Personlig udviklingstests",
  "Fremtidige opdateringer",
];

const yearlyFeatures = [
  "Alt i Monthly",
  "Fuldt sabbatårs roadmap",
  "Premium AI guidance",
  "Eksklusive guides til job og rejser",
  "Fremtidige Pro funktioner",
];

const PricingSection = () => {
  const { ref, isVisible } = useScrollAnimation(0.1);

  return (
    <section className="py-20 md:py-28 bg-background" ref={ref}>
      <div className="container max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="text-heading md:text-display-sm text-foreground mb-4">
            Vælg din plan
          </h2>
          <p className="text-lg text-muted-foreground max-w-md mx-auto">
            Start dit sabbatår med de rette værktøjer.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Monthly */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-2xl border border-border bg-card p-8 relative shadow-soft"
          >
            <h3 className="text-lg font-semibold text-foreground mb-1">MySabbat Pro Monthly</h3>
            <p className="text-sm text-muted-foreground mb-5">Perfekt hvis du vil prøve alle funktioner.</p>
            <p className="text-3xl font-bold text-foreground mb-6">
              39 kr<span className="text-base font-normal text-muted-foreground">/måned</span>
            </p>
            <ul className="space-y-3 mb-6">
              {monthlyFeatures.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-foreground">
                  <Check className="w-4 h-4 text-accent shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button asChild variant="outline" className="w-full rounded-xl">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Start månedligt</a>
            </Button>
          </motion.div>

          {/* Yearly – recommended */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-2xl border-2 border-accent bg-card p-8 relative shadow-lg glow-accent"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">
              Most popular
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">MySabbat Pro Yearly</h3>
            <p className="text-sm text-muted-foreground mb-5">Få hele sabbatåret planlagt til den bedste pris.</p>
            <p className="text-3xl font-bold text-foreground mb-6">
              349 kr<span className="text-base font-normal text-muted-foreground">/år</span>
            </p>
            <ul className="space-y-3 mb-6">
              {yearlyFeatures.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-foreground">
                  <Check className="w-4 h-4 text-accent shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button asChild className="w-full rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Start årsplan</a>
            </Button>
          </motion.div>
        </div>

        {/* Legal compliance */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 space-y-2"
        >
          <p className="text-xs text-muted-foreground">
            Abonnementet fornyes automatisk. Du kan opsige når som helst.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <Link to="/privacy" className="underline hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <span>·</span>
            <Link to="/terms" className="underline hover:text-foreground transition-colors">
              Terms of Use
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
